import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { RouteComponentProps, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { HomeFilled, ArrowLeftOutlined } from "@ant-design/icons";
import { SyncOutlined } from "src/icons";
import styles from "src/scenes/Imcom/Home/index.module.scss";
import PinnedContainer from "src/shared/components/ImCom/PinnedContainer";
import { fetchHome, searchPin, searchReset, FetchHomePayload, SearchPinPayload } from "src/store/home";
import { fetchAllOrgMetrics, FetchAllOrgMetricsPayload } from "src/store/allOrg";
import { setCategoriesFilters, categoryReset } from "src/store/category";
import { setFilters, clearDetails } from "src/store/details";
import {
  changeTimeTabAppWide,
  UserRoles,
  setIsRoleCalloutShownAction,
  setComingBackFromDetailsPage,
} from "src/store/app";
import { logoutAction } from "src/store/login";
import RibbonSlider from "src/shared/components/RibbonSlider";
import { useRedux } from "src/shared/hooks";
import Search from "src/shared/components/Search";
import { IRootState, IconClass } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import userService from "src/shared/services/user.service";
import { noop, safeGet } from "src/shared/utils";
import Permissions from "../../AllOrg/Permissions";
import { devLogging } from "src/utils/logger";
import { Button, Spin } from "antd";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import { getPodIdAndPodNameMapping } from "src/utils/getFilterOptions";
import { truncate, reduxOperationToStatus, mergeStatuses } from "src/shared/utils";
import FilterByPod from "src/scenes/Imcom/Home/FilterbyPod"; // Import the FilterByPod component

const CALL_DELAY = 1500;

interface IState {
  metrics: IReduxOperations;
  search: IReduxOperations;
  selectedTab: any;
  userRoles: IReduxOperations<UserRoles>;
  isRoleCalloutShown: boolean;
}

type MappedActionProps = {
  fetchHome(payload: FetchHomePayload);
  searchPin(payload: SearchPinPayload);
  categoryReset: IActionFactory<symbol, unknown>;
  searchReset: IActionFactory<symbol, unknown>;
  logoutAction();
  clearDetails: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setIsRoleCalloutShown: IActionFactory<symbol, boolean>;
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const MainContainer: React.FC<RouteComponentProps> = ({ history }) => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "IMCom Third Screen" });
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);
  const [page, setPage] = useState(1);
  const [timeTabForImCom, setTimeTabForImCom] = useState("LastHr_imcom");
  const containerRef = useRef<HTMLDivElement>(null as any);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const store_id_filter_value = queryParams.get("store_id") || "";
  const store_id_filter = store_id_filter_value
    ? [
        {
          field: "store_id",
          value: [store_id_filter_value],
        },
      ]
    : [];

  const [selectedPod, setSelectedPod] = useState<string[] | null>([store_id_filter_value]);
  const showDetails = useCallback(
    (id, name) => {
      mappedProps.setComingBackFromDetailsPage(true);

      history.push(`/im-com-app/details/fourth-screen/pin/${store_id_filter_value}/${id}`);
      ReactGA.event({
        category: "Pin Clicked on L1a",
        label: `Pin: ${name} on L1a`,
        action: "Click from L1a",
      });
    },
    [history]
  );

  const [showRibbon, setShowRibbon] = useState(false);

  const { clearDetails: clearL3Data, ...mappedProps } = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      metrics: state.home.pinnedMetrics.metrics,
      search: state.home.pinnedMetrics.search,
      loadMorePending: state.home.pinnedMetrics.loadMorePending,
      chartsDataPending: state.home.pinnedMetrics.chartsDataPending,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      userRoles: state.app.userRoles,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    {
      fetchHome,
      searchPin,
      searchReset,
      logoutAction,
      categoryReset,
      clearDetails,
      changeTimeTabAppWide,
      setIsRoleCalloutShown: setIsRoleCalloutShownAction,
      fetchAllOrgMetrics,
      setComingBackFromDetailsPage,
    }
  );

  const handleChangeTab = useCallback(
    ({ label }: { label: string }) => {
      setPage(1);
      mappedProps.changeTimeTabAppWide(label);
      setTimeTabForImCom(label);
    },
    [mappedProps]
  );

  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    mappedProps.logoutAction();
  }, []);

  const loadMore = useCallback(() => {
    if (
      !(safeGet(mappedProps, "metrics.pending", false) || safeGet(mappedProps, "loadMorePending", false)) &&
      safeGet(mappedProps, "metrics.payload.hasMore", false)
    ) {
      const filters = [
        {
          field: "store_id",
          value: selectedPod && selectedPod.length > 0 ? selectedPod : mappedProps.userRoles.payload?.pods || [],
        },
      ];
      mappedProps.fetchHome({ pageNo: page + 1, filters });
      setPage(page + 1);
    }
  }, [mappedProps.metrics, safeGet(mappedProps, "loadMorePending"), page]);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [shouldShowPermissions, setShouldShowPermissions] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [visibleMetrics, setVisibleMetrics] = useState(mappedProps.metrics);

  useEffect(() => {
    setVisibleMetrics(mappedProps.metrics);
  }, [mappedProps.metrics]);

  // To avoid few side effects of state change on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handlePodChange = useCallback(
    (value: string[]) => {
      setSelectedPod(value);

      devLogging("selected pod values", value);

      const filters =
        value.length > 0
          ? [
              {
                field: "store_id",
                value: value,
              },
            ]
          : [
              {
                field: "store_id",
                value: mappedProps.userRoles.payload?.pods || [],
              },
            ];
      setPage(1);
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters });
    },
    [mappedProps.fetchHome, mappedProps.userRoles.payload?.pods]
  );

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending") === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending"), isRefreshClicked]);

  useEffect(() => {
    mappedProps.changeTimeTabAppWide(timeTabForImCom);
  }, [timeTabForImCom]);

  useEffect(() => {
    if (store_id_filter.length > 0) {
      handlePodChange(store_id_filter[0].value);
    }
  }, [timeTabForImCom]);

  type OptionsType = {
    className: string;
    iconClass: string;
    handleClick(): void;
  };
  type OptionGroupType = Array<OptionsType>;
  const options: OptionGroupType = [
    {
      className: "option1",
      iconClass: "home",
      handleClick: () => {
        history.push("/im-com-app");
        window.location.reload();
      },
    },
    {
      className: "option3",
      iconClass: "sync",
      handleClick: () => {
        setIsRefreshClicked(Date.now());
        setPage(1);
        mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: store_id_filter });
      },
    },
  ];
  const onBack = useCallback(() => {
    // toggleComingBackFromDetailsPage(true);
    history.goBack();
  }, [history]);

  const meticOptionsV1 = useMemo(() => {
    return mappedProps.metrics.payload?.data.map((metric) => ({
      value: metric.label,
      label: metric.label,
    }));
  }, [mappedProps.metrics]);

  return (
    <>
      <div className={styles["container"]}>
        <TopNavbar
          isHome={false}
          handleClick={onBack}
          headerText={store_id_filter_value + ""}
          rootStyle="padding: 10px; color: black; "
        >
          <p style={{ marginLeft: -1 }}>
            {store_id_filter_value} - {/*@ts-ignore*/}
            {truncate(getPodIdAndPodNameMapping(mappedProps.userRoles?.payload)[store_id_filter_value], 20)}
          </p>
          {options.map((option) => (
            <MenuItem key={option.iconClass} {...option} showMarker={option.iconClass === "sliders"} />
          ))}
          {meticOptionsV1 && (
            <FilterByPod
              selectedPod={selectedPod || undefined}
              handlePodChange={handlePodChange}
              podOptions={[]} // Empty array to disable pod filter
              cityOptions={[]} // Empty array to disable city filter
              selectedCity={undefined}
              handleSelectedCity={() => {}}
              filterType=""
              setFilterType={() => {}}
              disablePodFilter={true}
              disableCityFilter={true}
              disableClearFilterButton={true}
            />
          )}
        </TopNavbar>
        {isInitialLoading ? (
          <Spin style={{ display: "block", margin: "20px auto" }} />
        ) : (
          <div className={styles["pinned-items"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <PinnedContainer
              isPulledUp={showRibbon}
              pins={visibleMetrics}
              showDetails={showDetails}
              isPending={safeGet(mappedProps, "metrics.pending", false)}
              onChangeTab={handleChangeTab}
              switchToManageMetrics={() => {}}
              selectedTab={mappedProps.selectedTab}
              brokenPodsVisibleTabs={[]}
            />
            {safeGet(mappedProps, "metrics.payload.hasMore", false) && (
              <Button type="primary" shape="round" className={styles["load-more-button"]} onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MainContainer;
